import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Upload } from "lucide-react";
import { ChartExpandModal } from "./ChartExpandModal";
import { ChartTitleRow } from "./ChartTitleRow";
import { ChartSegmentedControl } from "./ChartSegmentedControl";
import { PRIMARY_RED, CHART_BASELINE, formatCompactNumber } from "./chartPalette";
import { UserAvatar } from "@/components/ui/user-avatar";
import type {
  TimeRange,
  UploadPeriod,
  UserUpload,
} from "@/hooks/buildings/useBuildingDocumentStats";
import type { Translations } from "@/lib/i18n/types";

interface UploadActivityChartProps {
  data: UploadPeriod[];
  userBreakdown: Map<string, UserUpload[]>;
  timeRange: TimeRange;
  onTimeRangeChange: (r: TimeRange) => void;
  isLoading: boolean;
  t: Translations;
}

function formatPeriodLabel(period: string): string {
  // "18 Jun" — day-first, matching the dashboard's flat-timeline axis labels.
  const d = new Date(period + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function getDisplayName(firstName: string, lastName: string, email?: string | null): string {
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  return email ?? "";
}

interface ActivityTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ value?: number | string; payload?: UploadPeriod }>;
  userBreakdown: Map<string, UserUpload[]>;
}

function CustomTooltipContent({ active, payload, userBreakdown }: ActivityTooltipProps) {
  if (!active || !payload?.length) return null;

  const period = payload[0]?.payload?.period ?? "";
  const count = Number(payload[0]?.value ?? 0);
  const users = userBreakdown.get(period) ?? [];
  const displayLabel = formatPeriodLabel(period);

  return (
    <div className="rounded-lg border bg-white p-3 shadow-md" style={{ minWidth: 180 }}>
      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="font-medium">{displayLabel}</span>
        <span className="font-semibold">{count} uploads</span>
      </div>
      {users.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {users.map((user: UserUpload) => (
            <div key={user.userId} className="flex items-center gap-2 text-xs">
              {/* Same avatar treatment as the document list: the uploader's
                  profile picture when they have one, initials on their stored
                  colour otherwise. */}
              <UserAvatar
                user={{
                  id: user.userId,
                  first_name: user.firstName,
                  last_name: user.lastName,
                  email: user.email ?? "",
                  avatar_url: user.avatarUrl,
                  color: user.color,
                }}
                size={20}
                className="flex-shrink-0"
              />
              <span className="flex-1 truncate text-muted-foreground">
                {getDisplayName(user.firstName, user.lastName, user.email)}
              </span>
              <span className="font-medium tabular-nums">{user.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeRangeToggle({
  value,
  onChange,
  t,
}: {
  value: TimeRange;
  onChange: (r: TimeRange) => void;
  t: Translations;
}) {
  const d = t.buildingDetail.dashboard;
  return (
    <ChartSegmentedControl
      options={[
        { value: "30d", label: d.range30d },
        { value: "90d", label: d.range90d },
        { value: "1y", label: d.range1y },
      ]}
      value={value}
      onChange={onChange}
    />
  );
}

function ActivityChart({
  data,
  userBreakdown,
  timeRange,
  t,
  height = 200,
}: {
  data: UploadPeriod[];
  userBreakdown: Map<string, UserUpload[]>;
  timeRange: TimeRange;
  t: Translations;
  height?: number;
}) {
  const d = t.buildingDetail.dashboard;

  if (data.length === 0) {
    // Proper centred empty state instead of a blank chart area. The "try a
    // longer range" hint disappears at 1y — there is no longer range to try.
    const emptyTitle = {
      "30d": d.noUploads30d,
      "90d": d.noUploads90d,
      "1y": d.noUploads1y,
    }[timeRange];
    return (
      <div
        style={{ height }}
        className="flex flex-col items-center justify-center gap-1 text-center"
      >
        <Upload className="mb-1 h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-medium text-neutral-700">{emptyTitle}</p>
        {timeRange !== "1y" && <p className="text-xs text-muted-foreground">{d.noUploadsHint}</p>}
      </div>
    );
  }

  // Flat timeline: one brand-red line over a solid baseline with date labels
  // along the axis — no dashed grid. Every plotted period IS an upload day
  // (empty days are never bucketed), so a dot on each point marks the days
  // that have uploads. The Y axis keeps minimal tick labels for scale.
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 8, bottom: 5, left: 0 }}>
        <XAxis
          dataKey="period"
          tickFormatter={formatPeriodLabel}
          tick={{ fontSize: 11, fill: "#808080" }}
          axisLine={{ stroke: CHART_BASELINE }}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: "#808080" }}
          axisLine={false}
          tickLine={false}
          width={40}
          tickFormatter={formatCompactNumber}
        />
        <Tooltip
          content={<CustomTooltipContent userBreakdown={userBreakdown} />}
          position={{ y: 0 }}
          isAnimationActive={false}
          cursor={{ stroke: CHART_BASELINE }}
        />
        <Line
          type="monotone"
          dataKey="count"
          stroke={PRIMARY_RED}
          strokeWidth={2}
          isAnimationActive={false}
          dot={{ r: 3, fill: PRIMARY_RED, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: PRIMARY_RED, strokeWidth: 0 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function UploadActivityChart({
  data,
  userBreakdown,
  timeRange,
  onTimeRangeChange,
  isLoading,
  t,
}: UploadActivityChartProps) {
  const [expanded, setExpanded] = useState(false);
  const d = t.buildingDetail.dashboard;

  return (
    <>
      <div className="flex h-full flex-col">
        <ChartTitleRow
          title={d.uploadActivity}
          controls={<TimeRangeToggle value={timeRange} onChange={onTimeRangeChange} t={t} />}
          onExpand={() => setExpanded(true)}
          expandLabel={d.expandChart}
        />
        {isLoading ? (
          <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <div className="pt-3">
            <ActivityChart data={data} userBreakdown={userBreakdown} timeRange={timeRange} t={t} />
          </div>
        )}
      </div>

      <ChartExpandModal open={expanded} onOpenChange={setExpanded} title={d.uploadActivity}>
        <div className="flex items-center gap-2 pb-4">
          <TimeRangeToggle value={timeRange} onChange={onTimeRangeChange} t={t} />
        </div>
        <ActivityChart
          data={data}
          userBreakdown={userBreakdown}
          timeRange={timeRange}
          t={t}
          height={350}
        />
      </ChartExpandModal>
    </>
  );
}
